import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService, IStateAndParams } from './shared/services/auth/auth.service';
import { IExtendedDocument, IExtendedWindow, IObject } from '@type_definitions/datatypes';
import { IUserData } from './shared/services/auth/auth-data.service';
import { DOCUMENT } from '@angular/common';
import { BrowserVendorService } from './shared/browser/browser-vendor.service';
import { BreakpointsService } from './shared/services/breakpoints.service';
import { GaEventService, IGAEvent } from './shared/services/ga-event.service';
import { ConnectionService } from './shared/services/auth/connection.service';
import { STATE_HOME_PAGE, STATE_LOGIN } from './shared/app-states';
import { StateService, TransitionPromise } from '@uirouter/angular';
import { LanguageService } from './shared/language/language.service';
import { TranslateService } from '@ngx-translate/core';
import { GaMessageService } from './shared/message/ga-message.service';
import { AppStatesService } from './app-states.service';
import { GaWebStorageService, STORAGE_LOGIN, STORAGE_PROFILE_ID } from './shared/services/storage/ga-web-storage.service';
import { AnalyticsService } from './shared/services/analytics.service';
import { UIRouter } from '@uirouter/core';
import { UrlService } from './shared/browser/url.service';
import { EVENT_TRANSLATION_UPDATED } from './shared/app-events';
import { TranslationCategory } from './shared/services/ga-state.service';
import { TranslationHelperService } from './shared/translation/translation-helper.service';
import { AppTransitionService } from './app-transition.service';
import * as _ from 'underscore';
import { LovService } from '@app/shared/services/lov.service';
import { Lang } from '@app/shared/language/language-data.service';

@Component({
  selector: 'ga2-app',
  template: `
    <div class="main-view" ui-view="main" *ngIf="isReady"></div>
  `
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  public isReady:boolean;

  private currentUser:IUserData;
  private contentLoadedTimeout:any;
  private onAuthenticatedSubscription:Subscription;
  private onAuthFailureSubscription:Subscription;
  private onCurrentUserUpdatedSubscription:Subscription;
  private eventsSubscription:Subscription;

  constructor(
    @Inject(Window) private window:IExtendedWindow,
    @Inject(DOCUMENT) private document:IExtendedDocument,
    private authService:AuthService,
    private browserVendorService:BrowserVendorService,
    private breakpointsService:BreakpointsService,
    private eventService:GaEventService,
    private stateService:StateService,
    private languageService:LanguageService,
    private translateService:TranslateService,
    private messageService:GaMessageService,
    private appStatesService:AppStatesService,
    private connectionService:ConnectionService,
    private webStorage:GaWebStorageService,
    private analyticsService:AnalyticsService,
    private translationHelperService:TranslationHelperService,
    private uiRouter:UIRouter,
    private urlService:UrlService,
    private appTransitionService:AppTransitionService,
    private lovService:LovService
  ) {
    /**/
  }

  public ngOnInit():void {
    // register listener
    this.listen();

    this.authService.activate(this.appTransitionService.unprotectedUrl?.noAutoAuthentication)
      .then(() => {
        this.currentUser = this.authService.getCurrentUser();
      })
      .finally(() => {
        this.isReady = true;
      });
  }

  public ngAfterViewInit():void {
    // prevent debouncing
    this.window.clearTimeout(this.contentLoadedTimeout);
    this.contentLoadedTimeout = this.window.setTimeout(():void => {
      const userId:string = this.webStorage.local.get(STORAGE_PROFILE_ID) + ': ';
      const userName:string = this.webStorage.local.get(STORAGE_LOGIN) || 'anonymous user';
      this.analyticsService.ga('send', 'pageview', {
        hitType: 'pageview',
        page: this.window.location.href,
        'userId': userId + userName
      });
    }, 500);
  }

  private listen():void {
    $(this.document).ready(this.onDocumentReady);
    this.onAuthenticatedSubscription = this.authService.onAuthenticated.subscribe(this.changeStateAfterAuthentication);
    this.onCurrentUserUpdatedSubscription = this.authService.onCurrentUserUpdated.subscribe(this.onCurrentUserUpdated);
    this.eventsSubscription = this.eventService.events.subscribe((event:IGAEvent) => {
      if (event.name === ConnectionService.EVENT_SESSION_DESTROYED) {
        this.onSessionDestroyed();
      }
      else if (event.name === EVENT_TRANSLATION_UPDATED) {
        this.onTranslationChange();
      }
    });
  }

  private onDocumentReady = ():void => {
    console.debug(`Hello GA2, DOM ready: ${this.browserVendorService.name}`);
    $(this.document).find('html').addClass(this.browserVendorService.classes);
    this.breakpointsService.start();
  };

  private changeStateAfterAuthentication = (authenticated:boolean):void => {
    if (authenticated) {
      this.currentUser = this.authService.getCurrentUser();
      const currentParams:IObject = this.urlService.urlParams;

      // this will trigger when it's not in unprotected states and protected states
      if (!this.appTransitionService.unprotectedUrl?.noStateChangeAfterAuthentication) {
        const toState:IStateAndParams = this.authService.getStateAfterLogin();
        let toStatePromise:TransitionPromise;
        if (!toState) {
          toStatePromise = this.stateTo(STATE_HOME_PAGE);
        } else {
          toStatePromise = this.stateTo(toState.stateName, toState.stateParams);
        }

        toStatePromise
          .then(() => {
            // this will trigger when it's not in unprotected states
            if (!this.appTransitionService.unprotectedUrl && (currentParams.token || currentParams.acslToken)) {
              // add timeout here to let angular digest current stateParams redirection
              // remove token and acslToken without reloading the page
              const params:IObject = _.extend({}, this.uiRouter.globals.params);
              delete params.acslToken;
              delete params.token;

              this.window.setTimeout(() => {
                this.stateService.go(this.uiRouter.globals.current.name, params, { reload: false, inherit: false, location: true });
              });
            }
          });
      }

      this.lovService.loadAllLists(this.userLanguage);
      this.switchToUserLang();
      this.setSkinCSS();
    } else {
      const ssoLogoutUrl:string = this.currentUser?.ssoLogoutUrl;
      if (ssoLogoutUrl) {
        console.warn(`Not-authenticated: redirect to ${ssoLogoutUrl}`);
        this.window.location.href = ssoLogoutUrl;
      } else {
        console.warn(`Not-authenticated: transition to ${STATE_LOGIN} state`);
        this.stateTo(STATE_LOGIN);
      }
    }
  };

  private onCurrentUserUpdated = (currentUser:IUserData):void => {
    this.currentUser = currentUser;
    this.lovService.loadAllLists(this.userLanguage);
    this.setSkinCSS();
    this.switchToUserLang();
  };

  private onSessionDestroyed = ():void => {
    const promise:Promise<void> = Promise.resolve();
    if (this.stateService.current.name === STATE_HOME_PAGE) { // only show the connection loss dialog when we're on a GA1 screen
      this.messageService.showWarningDialog('APP.SESSION_LOST.TITLE', 'APP.SESSION_LOST.TEXT');
    }

    promise
      .finally(() => this.authService.createNewSession())
      .then(() => {
        if (this.appStatesService.isGA1LoginState(this.stateService.current.name, this.stateService.params)) {
          console.warn('GA1 login state => redirect to homepage');
          //go to homepage
          this.stateService.go(STATE_HOME_PAGE, undefined, { inherit: false });
        }
      })
      .then(() => this.connectionService.startPolling());
  };

  private onTranslationChange = ():void => {
    if (this.languageService.uiLanguage) {
      // the right UI language is decided in onBeforeTransition()
      // we catch the moment after the UI language has been already decided and notify the translateService to start rendering the UI
      this.translationHelperService.setReady();
    }
  };

  private switchToUserLang():void {
    const userLanguage:string = this.userLanguage;
    if (userLanguage !== this.languageService.getUserLanguage()) {
      this.languageService.saveLanguage(userLanguage);
    }

    // right after save switch to the language selected within the user profile
    if (userLanguage !== this.translateService.currentLang) {
      this.translateService.use(userLanguage);
      // reload translations
      const modules:TranslationCategory[] = this.stateService.current.data?.modules || this.stateService.$current.parent?.data?.modules || [];
      modules.unshift(TranslationCategory.GA, TranslationCategory.APP);
      this.translationHelperService.loadTranslations({ ...this.stateService.current.data, modules }, true);
    }
  }

  private setSkinCSS():void {
    const skinCode:string = this.authService.getSkinCode();
    if (this.window.setSkin) {
      this.window.setSkin(skinCode);
    }
  }

  private stateTo(stateName:string, toParams?:IObject):TransitionPromise {
    if (stateName !== this.stateService.current.name) {
      return this.stateService.transitionTo(stateName, toParams, {
        location: true,
        inherit: true,
        relative: this.stateService.current
      });
    }

    return <any>Promise.resolve();
  }

  private get userLanguage():string {
    return this.currentUser?.language?.isoCode || Lang.EN;
  }

  public ngOnDestroy():void {
    this.onCurrentUserUpdatedSubscription?.unsubscribe();
    this.onAuthenticatedSubscription?.unsubscribe();
    this.onAuthFailureSubscription?.unsubscribe();
    this.eventsSubscription?.unsubscribe();
  }
}